import { Link } from 'react-router-dom';
import { 
  MessageSquare, 
  Shield, 
  Activity, 
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useIntegrations } from '../hooks/useIntegrations';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function Dashboard() {
  const { state } = useApp();
  const { integrations, loading } = useIntegrations();

  const stats = [
    {
      name: 'Connected Accounts',
      value: integrations.filter(i => i.status === 'active').length,
      icon: MessageSquare,
      color: 'text-primary-600',
      bg: 'bg-primary-100',
    },
    {
      name: 'Total Messages',
      value: '1,234', // Mock data
      icon: Activity,
      color: 'text-success-600',
      bg: 'bg-success-100',
    },
    {
      name: 'Alerts Generated',
      value: '3', // Mock data
      icon: AlertTriangle,
      color: 'text-warning-600',
      bg: 'bg-warning-100',
    },
    {
      name: 'Safe Conversations',
      value: '98%', // Mock data
      icon: Shield,
      color: 'text-success-600',
      bg: 'bg-success-100',
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-success-500" />;
      case 'connecting':
        return <Clock className="h-5 w-5 text-warning-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-error-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'error':
        return 'Error';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back, {state.user?.first_name}! Here's what's happening with your Chat Safe monitoring.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`p-3 rounded-md ${stat.bg}`}>
                        <Icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {stat.name}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stat.value}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Connected Accounts Overview */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Connected Accounts
                </h3>
                <Link
                  to="/integrations"
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Integration
                </Link>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" className="text-primary-600" />
                </div>
              ) : integrations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No integrations</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by connecting your first chat platform.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/integrations"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Integration
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {integrations.slice(0, 3).map((integration) => (
                    <div
                      key={integration.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <MessageSquare className="h-8 w-8 text-green-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {integration.type}
                          </p>
                          <p className="text-xs text-gray-500">
                            {integration.config?.phone_number || 'Not connected'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {getStatusIcon(integration.status)}
                        <span className="ml-2 text-sm text-gray-600">
                          {getStatusText(integration.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {integrations.length > 3 && (
                    <div className="text-center pt-2">
                      <Link
                        to="/integrations"
                        className="text-sm text-primary-600 hover:text-primary-500"
                      >
                        View all {integrations.length} integrations
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Recent Activity
              </h3>
              
              <div className="space-y-4">
                {/* Mock activity items */}
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-success-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      WhatsApp integration connected successfully
                    </p>
                    <p className="text-xs text-gray-500">2 minutes ago</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Activity className="h-5 w-5 text-primary-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      Processed 45 new messages
                    </p>
                    <p className="text-xs text-gray-500">1 hour ago</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-success-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      All conversations marked as safe
                    </p>
                    <p className="text-xs text-gray-500">3 hours ago</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <Link
                  to="/alerts"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  View all activity →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
